import * as Cookies from 'js-cookie'
import { v4 as uuidv4 } from 'uuid'
import _ from 'lodash'
import * as constants from '@adc/constants'
import configment from '@adc/configment'
import responseParsers from './responseParsers'
import timeout from './timeout'
import * as common from './common'
import type {
  AdcFetch,
  PatchedResponse,
  BrowserFetchOptions,
  FetchOptions,
  GraphVariantOptions,
} from './types'
import * as types from './types'
import graphFactory from './graph'
import withProgress from './withProgress'
import { usesProxy, generateProxyRoute } from './proxyRoutes'
import wellKnownSymbols from './wellKnownSymbols'

const defaultBrowserOpts = Object.freeze({
  ...types.defaultFetchOptions,
  credentials: 'same-origin',
  useProxy: true,
})

interface StringIndexable {
  [index: string]: any
}

/**
 * This is somewhat arbitrary, but this value was arrived at by observing what our timeouts were set to in different
 * applications over time. Anywhere between 6-10 seconds was normal for most apps. We have had longer timeouts in the
 * past, and regretted it when services when down in production and requests weren't failing fast enough.
 */
const DEFAULT_FETCH_TIMEOUT: number = 8000

const patchedFetch: AdcFetch = function (
  url: string,
  passedOptions: BrowserFetchOptions = {}
): Promise<PatchedResponse> {
  const requestController = passedOptions.controller || new AbortController()

  // These options are unique. We can't define them in the default object because their default value allocation
  // includes calls to the configuration library, which is not guaranteed to be loaded when this module first loads, but
  // should be loaded once this module is called.
  passedOptions.requestTimeout = Number(
    passedOptions.requestTimeout ||
      configment.get('REQUEST_TIMEOUT', DEFAULT_FETCH_TIMEOUT)
  )

  let options: BrowserFetchOptions = _.merge(
    _.cloneDeep(defaultBrowserOpts),
    passedOptions
  )

  /**
   * Generate this on the client so it can be traced through the services
   */
  ;(options.headers as StringIndexable)[constants.headers.CORRELATION_HEADER] =
    uuidv4()

  /**
   * XSRF protection
   */
  const xsrfValue = Cookies.get(constants.cookies.XSRF_COOKIE)

  if (xsrfValue) {
    ;(options.headers as StringIndexable)[constants.headers.XSRF_HEADER] =
      xsrfValue
  }

  // If an internal host is set, we accept the first argument to be a path instead
  // @ts-ignore
  if (_.get(this as any, '__internal.host')) {
    // @ts-ignore
    url = this.__internal.host + url
  }

  if (options.useProxy && usesProxy(url)) {
    url = generateProxyRoute(url).route
  }

  url = common.constructUrl(url, options.queryParams)

  common.stripNonStandardFetchOptions(options)
  common.attemptAutomaticBodySerialization(options)
  ;(options.headers as StringIndexable)['user-agent'] =
    (options.headers as StringIndexable)['user-agent'] || common.userAgent()

  let {
    tries,
    retryTimeout,
    requestTimeout,
    eventCapture,
    parseResponse,
    queryParams,
    ...fetchOptions
  } = options

  // Async request aborting, either from the caller, or because we timed out
  ;(fetchOptions as RequestInit).signal = requestController.signal

  return timeout(
    common.executeRequest(url, fetchOptions as RequestInit, {
      tries,
      retryTimeout,
      eventCapture,
      parseResponse,
    }),
    requestTimeout || DEFAULT_FETCH_TIMEOUT,
    requestController
  )
}

patchedFetch.get = common.methodFactory(patchedFetch, 'GET')
patchedFetch.post = common.methodFactory(patchedFetch, 'POST')
patchedFetch.put = common.methodFactory(patchedFetch, 'PUT')
patchedFetch.patch = common.methodFactory(patchedFetch, 'PATCH')
patchedFetch.delete = common.methodFactory(patchedFetch, 'DELETE')
patchedFetch.__internal = {}

const forAuctionGraph = graphFactory(patchedFetch)
const fromService = common.boundedFetchFactory(patchedFetch)
const serializeParams = common.serializeParams

// This is for backwards compat since in commonjs we attached these
// to the root export, which would be the default export here
patchedFetch.forAuctionGraph = forAuctionGraph
patchedFetch.fromService = fromService
patchedFetch.withProgress = withProgress
patchedFetch.serializeParams = serializeParams
patchedFetch.responseParsers = responseParsers

export default patchedFetch
export {
  forAuctionGraph,
  fromService,
  serializeParams,
  responseParsers,
  types,
  withProgress,
  FetchOptions,
  PatchedResponse,
  GraphVariantOptions,
  wellKnownSymbols,
}
